import { useEffect, useMemo, useRef, useState } from "react";
import type { Bank, HalftoneDetailLevel, HalftoneResponse, HalftoneStyle, VietQRData } from "../api";
import { ApiError, createHalftoneQr } from "../api";
import { formatAccountNumberDisplay, formatAmount } from "../format";
import { detectInAppBrowser, downloadImageWithFallback } from "../env";
import ScanabilityBadge from "./ScanabilityBadge";
import FullscreenImageGuide from "./FullscreenImageGuide";

type Props = {
  fileId: string;
  bank: Bank;
  accountNumber: string;
  amount: number | undefined;
  qrData: VietQRData;
  onBack: () => void;
  onChangePhoto: () => void;
  onReset: () => void;
};

/**
 * Dải thanh trượt "Độ rõ ảnh" — khớp đúng `blendStrength` server dùng, nhưng KHÔNG hiện số
 * này cho người dùng (docs/71-consumer-ux.md §7 nguyên tắc "không hiện số/thuật ngữ kỹ
 * thuật"). Sàn 0.45 là sàn an toàn quét đo được bằng thực nghiệm cho chế độ banking (xem
 * `apps/api/src/routes/uploads.ts` HALFTONE_BLEND_FLOOR) — không cho người dùng phổ thông
 * kéo xuống thấp hơn (đó là lựa chọn "chấp nhận rủi ro" chỉ dành cho chế độ Nâng cao, chưa
 * có ở nhiệm vụ này).
 *
 * ĐÃ KIỂM LẠI cho thuật toán halftone v2 ("uniform-k", modulePx mặc định server nay là 24 —
 * xem HALFTONE_DEFAULT_MODULE_PX): sàn 0.45 vẫn ĐÚNG (sweep riêng cho v2 xác nhận an toàn
 * liên tục 0.45→0.675+ đúng ở modulePx=24, xem packages/creative-renderer/test/halftone.test.ts
 * mục "TÌM CỬA SỔ blendStrength AN TOÀN") — không hạ hay nâng floor. Trần 0.85 KHÔNG dựa trên
 * "vùng đã biết hỏng" (chưa từng sweep tới 0.85 dù v1 hay v2) mà là lựa chọn UX giữ lại chút
 * ảnh hiển thị — giữ nguyên vì hướng blendStrength càng cao càng AN TOÀN HƠN (đẩy trung bình
 * module về gần cực trị đen/trắng, không giống modulePx vốn không đơn điệu), nên không phải
 * rủi ro quét như modulePx.
 */
const MIN_BLEND = 0.45;
const MAX_BLEND = 0.85;
const DEFAULT_BLEND = 0.55;
const BLEND_STEP = 0.05;
const REPAIR_STEP = 0.15;
const SLIDER_DEBOUNCE_MS = 450;

/**
 * "Độ chi tiết ảnh" — ép QR dùng nhiều module hơn để ảnh nền hiện rõ hơn (xem
 * apps/api/src/render/halftone-detail-level.ts cho lý giải kỹ thuật đầy đủ + nguồn số liệu).
 * Nhãn/mô tả CỐ Ý dùng tiếng Việt đời thường, KHÔNG hiện tên kỹ thuật normal/fine/ultra hay số
 * module/version cho người dùng (cùng nguyên tắc "không hiện số/thuật ngữ kỹ thuật" như thanh
 * "Độ rõ ảnh" bên dưới, docs/71-consumer-ux.md §7).
 */
const DETAIL_LEVEL_OPTIONS: { value: HalftoneDetailLevel; label: string; hint: string }[] = [
  { value: "normal", label: "Vừa phải", hint: "Dễ quét nhất, ảnh hơi thô" },
  { value: "fine", label: "Rõ", hint: "Cân bằng (khuyên dùng)" },
  { value: "ultra", label: "Rất rõ", hint: "Ảnh đẹp nhất, cần in hoặc hiển thị to hơn" },
];
/** Khớp DEFAULT_HALFTONE_DETAIL_LEVEL ở server (apps/api/src/render/halftone-detail-level.ts). */
const DEFAULT_DETAIL_LEVEL: HalftoneDetailLevel = "fine";

/**
 * "Kiểu trộn ảnh" — hai kỹ thuật ĐỘC LẬP (xem apps/api/src/schemas.ts halftoneStyleSchema +
 * doc comment đầu route halftone trong apps/api/src/routes/uploads.ts). MẶC ĐỊNH 'photo' (khớp
 * đúng ảnh mẫu chủ dự án cung cấp — giữ ảnh gốc nguyên vẹn, chỉ vẽ chấm nhỏ mang dữ liệu).
 */
const STYLE_OPTIONS: { value: HalftoneStyle; label: string; hint: string }[] = [
  { value: "photo", label: "Giữ nguyên ảnh", hint: "Nhìn rõ ảnh nhất (khuyên dùng)" },
  { value: "blend", label: "Hoà vào mã", hint: "Mã chắc chắn quét được hơn" },
];
const DEFAULT_STYLE: HalftoneStyle = "photo";

function halftoneErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 422) {
      return "Ảnh này có nhiều chi tiết vụn nên máy quét chưa đọc được mã. Bạn thử chọn ảnh khác nhé — ảnh rõ mặt, tương phản cao, ít hoa văn sẽ dễ quét hơn.";
    }
    if (err.status === 404) {
      return "Tính năng trộn ảnh vào mã QR đang được hoàn thiện ở phía máy chủ — bạn quay lại thử sau ít phút nhé.";
    }
    if (err.message === "network") {
      return "Mạng đang chập chờn, mình chưa trộn được ảnh vào mã QR. Thử lại giúp mình nhé.";
    }
  }
  return "Có lỗi xảy ra khi trộn ảnh vào mã QR. Thử lại giúp mình nhé.";
}

/** Luồng "Trộn ảnh vào mã QR" (halftone). */
export default function PhotoHalftoneStep({ fileId, bank, accountNumber, amount, qrData, onBack, onChangePhoto, onReset }: Props) {
  const [blend, setBlend] = useState(DEFAULT_BLEND);
  const [detailLevel, setDetailLevel] = useState<HalftoneDetailLevel>(DEFAULT_DETAIL_LEVEL);
  const [style, setStyle] = useState<HalftoneStyle>(DEFAULT_STYLE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<HalftoneResponse | null>(null);
  const [version, setVersion] = useState(0);

  const [busy, setBusy] = useState(false);
  const [downloadMsg, setDownloadMsg] = useState<string | null>(null);
  const [fullscreenGuide, setFullscreenGuide] = useState<string | null>(null);

  const debounceRef = useRef<number | null>(null);
  const inAppInfo = useMemo(() => detectInAppBrowser(), []);
  const fileBase = `qr-anh-${bank.shortName || bank.code}-${accountNumber}`.replace(/\s+/g, "");

  function runHalftone(blendValue: number, detailLevelValue: HalftoneDetailLevel, styleValue: HalftoneStyle) {
    setLoading(true);
    setError(null);
    createHalftoneQr(fileId, { qrData, blendStrength: blendValue, detailLevel: detailLevelValue, style: styleValue })
      .then((res) => {
        setResult(res);
        setVersion((v) => v + 1);
      })
      .catch((err: unknown) => {
        setResult(null);
        setError(halftoneErrorMessage(err));
      })
      .finally(() => setLoading(false));
  }

  // Chạy lần đầu với độ rõ + độ chi tiết + kiểu trộn ảnh mặc định — người dùng thấy kết quả
  // ngay không cần chỉnh gì.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    runHalftone(DEFAULT_BLEND, DEFAULT_DETAIL_LEVEL, DEFAULT_STYLE);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [fileId]);

  function handleSliderChange(e: React.ChangeEvent<HTMLInputElement>) {
    const v = Number(e.target.value);
    setBlend(v);
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => runHalftone(v, detailLevel, style), SLIDER_DEBOUNCE_MS);
  }

  function handleDetailLevelChange(next: HalftoneDetailLevel) {
    setDetailLevel(next);
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => runHalftone(blend, next, style), SLIDER_DEBOUNCE_MS);
  }

  function handleStyleChange(next: HalftoneStyle) {
    setStyle(next);
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => runHalftone(blend, detailLevel, next), SLIDER_DEBOUNCE_MS);
  }

  function handleRepair() {
    const next = Math.min(MAX_BLEND, blend + REPAIR_STEP);
    setBlend(next);
    runHalftone(next, detailLevel, style);
  }

  function handleDownload() {
    if (!result) return;
    setBusy(true);
    setDownloadMsg(null);
    fetch(`${result.imageUrl}?v=${version}`)
      .then((res) => {
        if (!res.ok) throw new Error("fetch_failed");
        return res.blob();
      })
      .then((blob) => downloadImageWithFallback(blob, `${fileBase}.png`, inAppInfo))
      .then((res) => {
        if (res.method === "share") setDownloadMsg('Đã mở bảng chia sẻ — chọn "Lưu vào Ảnh" để lưu nhé.');
        else if (res.method === "direct-download") setDownloadMsg("Đã lưu ảnh vào máy.");
        else setFullscreenGuide(res.imageUrl);
      })
      .catch(() => {
        setDownloadMsg("Chưa tải được ảnh. Bạn thử lại nhé, hoặc chạm giữ vào ảnh bên trên để lưu.");
      })
      .finally(() => setBusy(false));
  }

  const imgSrc = result ? `${result.imageUrl}?v=${version}` : null;

  return (
    <section className="step photo-halftone-step" aria-labelledby="step-photo-halftone-title">
      <button type="button" className="link-back" onClick={onBack}>
        ← Quay lại
      </button>

      <h1 id="step-photo-halftone-title" className="step-title">
        Trộn ảnh vào mã QR
      </h1>
      <p className="hint-text small">Ảnh rõ mặt, tương phản cao sẽ đẹp nhất.</p>

      <div className="photo-halftone-preview">
        {imgSrc ? (
          <img src={imgSrc} alt="Mã QR đã trộn ảnh" className="photo-halftone-img" />
        ) : (
          <div className="photo-halftone-placeholder" aria-hidden="true" />
        )}
        {loading ? (
          <div className="photo-stage-overlay-loading">
            <span>Đang trộn ảnh vào mã QR…</span>
          </div>
        ) : null}
      </div>

      {error ? (
        <p className="photo-adjust-warning">{error}</p>
      ) : result ? (
        <>
          <ScanabilityBadge label={result.scanability.label} bankingSafe={result.scanability.bankingSafe} onRepair={handleRepair} />
          {/* Server đã tự đổi 'photo' -> 'blend' vì ảnh này không quét được ở mọi mức chi tiết —
              luôn NÓI RÕ trước tiên (thay hẳn thông báo "đã chỉnh lại chút" chung chung bên
              dưới), không bao giờ đổi âm thầm (đúng nguyên tắc I5). */}
          {result.styleUsed !== result.style ? (
            <p className="photo-halftone-detail-note">Ảnh này hơi khó, mình đã đổi sang kiểu chắc chắn hơn nhé.</p>
          ) : result.autoAdjusted ? (
            <p className="hint-text small">Đã chỉnh lại chút cho dễ quét nhé.</p>
          ) : null}
          {/* Server đã tự hạ "độ chi tiết ảnh" (vd. Rất rõ -> Rõ) để đảm bảo mã quét được —
              luôn NÓI RÕ, không bao giờ hạ âm thầm (đúng nguyên tắc I5). */}
          {result.detailLevelUsed !== result.detailLevel ? (
            <p className="photo-halftone-detail-note">Ảnh này hơi khó, mình đã giảm độ chi tiết một chút để mã quét được nhé.</p>
          ) : null}
          {/* Chế độ "Giữ nguyên ảnh" ở vùng ranh giới với một số máy quét cũ hơn (đo được: ZXing —
              khớp hầu hết app ngân hàng — đọc tốt, jsQR thì không) — nói rõ để người dùng biết
              nên tự quét thử, không giấu. */}
          {result.styleUsed === "photo" && result.zxingOk && !result.jsqrOk ? (
            <p className="photo-halftone-detail-note">Mã đọc tốt với hầu hết app ngân hàng. Bạn nên quét thử trước khi dùng.</p>
          ) : null}
          {/* Mức "Rất rõ" dùng nhiều module hơn hẳn -> mỗi ô nhỏ hơn -> cần in/hiển thị to hơn để
              camera điện thoại đọc được. Chỉ hiện khi ảnh THỰC SỰ dùng mức này (không hiện nếu
              server đã hạ xuống mức khác, xem nhánh trên). */}
          {result.detailLevelUsed === "ultra" ? (
            <p className="photo-halftone-detail-note">
              Mã này nhiều ô nhỏ hơn — nên in từ khổ A5 trở lên hoặc hiển thị trên màn hình lớn. Khuyến nghị cạnh QR khi in tối
              thiểu khoảng {result.recommendedMinPrintSizeMm}mm ({result.recommendedMinPrintSizeNote})
            </p>
          ) : null}
        </>
      ) : null}

      <fieldset className="photo-halftone-style" disabled={loading}>
        <legend className="hint-text small">Kiểu trộn ảnh</legend>
        {STYLE_OPTIONS.map((opt) => (
          <label key={opt.value} className="photo-halftone-detail-option">
            <input
              type="radio"
              name="halftone-style"
              value={opt.value}
              checked={style === opt.value}
              onChange={() => handleStyleChange(opt.value)}
            />
            <span className="photo-halftone-detail-option-text">
              <strong>{opt.label}</strong>
              <span className="hint-text small">{opt.hint}</span>
            </span>
          </label>
        ))}
      </fieldset>

      <fieldset className="photo-halftone-detail-level" disabled={loading}>
        <legend className="hint-text small">Độ chi tiết ảnh</legend>
        {DETAIL_LEVEL_OPTIONS.map((opt) => (
          <label key={opt.value} className="photo-halftone-detail-option">
            <input
              type="radio"
              name="halftone-detail-level"
              value={opt.value}
              checked={detailLevel === opt.value}
              onChange={() => handleDetailLevelChange(opt.value)}
            />
            <span className="photo-halftone-detail-option-text">
              <strong>{opt.label}</strong>
              <span className="hint-text small">{opt.hint}</span>
            </span>
          </label>
        ))}
      </fieldset>

      {style === "blend" ? (
        <div className="photo-halftone-slider">
          <label htmlFor="halftone-blend-slider" className="hint-text small">
            Độ rõ ảnh
          </label>
          <input
            id="halftone-blend-slider"
            type="range"
            min={MIN_BLEND}
            max={MAX_BLEND}
            step={BLEND_STEP}
            value={blend}
            onChange={handleSliderChange}
            disabled={loading}
          />
          <div className="photo-halftone-slider-labels">
            <span>Ảnh rõ hơn</span>
            <span>Dễ quét hơn</span>
          </div>
        </div>
      ) : null}

      <div className="qr-summary">
        <strong className="qr-summary-bank">{bank.shortName}</strong>
        <span className="qr-summary-account">{formatAccountNumberDisplay(accountNumber)}</span>
        {amount !== undefined ? <span className="qr-summary-amount">{formatAmount(amount)} ₫</span> : null}
      </div>

      <p className="scan-reminder">Hãy tự quét thử bằng app ngân hàng của bạn trước khi đưa cho người khác.</p>

      <div className="result-actions">
        <button type="button" className="btn btn-primary btn-block" onClick={handleDownload} disabled={busy || !result}>
          {busy ? "Đang chuẩn bị ảnh…" : "Tải ảnh về"}
        </button>
        {downloadMsg ? <p className="hint-text small">{downloadMsg}</p> : null}

        <button type="button" className="btn btn-secondary btn-block" onClick={onChangePhoto}>
          Đổi ảnh khác
        </button>
        <button type="button" className="btn btn-secondary btn-block" onClick={onReset}>
          Tạo mã khác
        </button>
      </div>

      {fullscreenGuide ? (
        <FullscreenImageGuide
          imageUrl={fullscreenGuide}
          os={inAppInfo.os}
          onClose={() => {
            URL.revokeObjectURL(fullscreenGuide);
            setFullscreenGuide(null);
          }}
        />
      ) : null}
    </section>
  );
}
